// src/api/auth.tsx
// Auth context: säilyttää access tokenia muistissa, tarjoaa login/logout

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  adoptAccessToken,
  authApi,
  clearApiCache,
  clearTabUser,
  onOtherTabLogin,
  registerTabUser,
  setAccessToken,
  twoFactorApi,
} from './client';
import { clearLegacyAuthStorage } from '../styles/theme';
import type { LoginRequest, LoginResponseSuccess } from './types';
import { isLoginSuccess, isLoginRequires2fa, isLoginRequiresEnrollment } from './types';

/** Multi-tab-identiteettivahdin ilmoitus login-sivulle (4.8.2026) */
export const USER_CHANGED_NOTICE =
  'Toiseen välilehteen kirjauduttiin eri tunnuksella, joten tämä välilehti kirjattiin ulos turvallisuussyistä. Kirjaudu uudelleen.';

interface AuthState {
  isAuthenticated: boolean;
  // Sprint 35 / H1.B: allowedPages on lista pageKey:tä joille rooli on oikeutettu
  // (admin = kaikki). Käytetään TopNav-suodatuksessa + RequirePage-reittisuojauksessa.
  user: { id: string; email: string; fullName: string; role: string; allowedPages?: string[]; researchAccess?: boolean } | null;
  isLoading: boolean;
  /** Identiteettivahdin viesti (näytetään login-sivulla), null = ei ilmoitusta */
  authNotice: string | null;
}

/**
 * Login-funktion paluu-arvo. LoginPage tulkitsee tämän ja näyttää
 * sopivan jatkovaiheen UI:n:
 *   - 'ok' → kirjautuminen valmis, redirect dashboardiin
 *   - 'requires2fa' → näytä TOTP-koodin syöttölomake (twoFaToken pidetään muistissa)
 *   - 'requiresEnrollment' → näytä QR + enrollment-flow (enrollmentToken pidetään muistissa)
 */
export type LoginResult =
  | { status: 'ok' }
  | { status: 'requires2fa'; twoFaToken: string }
  | { status: 'requiresEnrollment'; enrollmentToken: string };

interface AuthContextValue extends AuthState {
  login: (creds: LoginRequest) => Promise<LoginResult>;
  /** Login-flow:n vaihe 2: lähetä TOTP-koodi twoFaToken:lla */
  completeTwoFactor: (twoFaToken: string, code: string, trustThisDevice?: boolean) => Promise<void>;
  /** Login-flow:n enrollment: pakotettu 2FA-enroll loginin jälkeen */
  completeInitialEnrollment: (
    enrollmentToken: string,
    code: string,
  ) => Promise<{ backupCodes: string[] }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Token refresh interval: 12 min (token kestää 15 min)
const REFRESH_INTERVAL_MS = 12 * 60 * 1000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    authNotice: null,
  });

  // Refresh-funktio — hakee uuden access tokenin ja käyttäjätiedot
  const doRefresh = useCallback(async (): Promise<boolean> => {
    let data: { accessToken: string };
    try {
      data = await authApi.refresh();
    } catch (err: any) {
      // Löydös F2: erottele VERKKOVIRHE aidosta istunnon vanhentumisesta.
      // Aiemmin mikä tahansa virhe (myös kuuluvuuskato proaktiivisessa 12 min
      // refreshissä) kirjasi operaattorin ulos ja mitätöi offline-jonon.
      const isAuthExpiry = err?.statusCode === 401 || err?.statusCode === 403;
      if (!isAuthExpiry) {
        // Verkkovirhe: säilytä nykyinen istunto jos se oli voimassa (yritetään taas
        // seuraavassa intervallissa); alkulatauksessa ilman yhteyttä jää kirjautumatta.
        setState(prev => prev.isAuthenticated
          ? { ...prev, isLoading: false }
          : { ...prev, isAuthenticated: false, user: null, isLoading: false });
        return false;
      }
      // Aito vanhentuminen (401/403) → kirjaa ulos
      setAccessToken(null);
      setState(prev => ({ ...prev, isAuthenticated: false, user: null, isLoading: false }));
      return false;
    }

    // Identiteettivahti: jos cookie kuuluu nyt eri käyttäjälle (toinen tab
    // kirjautui eri tunnuksella), hylkää token — auth:user-changed-käsittelijä
    // hoitaa tilan ja ilmoituksen.
    if (adoptAccessToken(data.accessToken) === 'user-changed') return false;
    const baseUrl = (import.meta as any).env?.VITE_API_BASE_URL ?? 'http://localhost:3001/api';
    const me = await fetch(baseUrl + '/auth/me', {
      headers: { Authorization: `Bearer ${data.accessToken}` },
      credentials: 'include',
    }).then(r => r.ok ? r.json() : null).catch(() => null);
    setState(prev => ({ ...prev, isAuthenticated: true, user: me ?? null, isLoading: false }));
    return true;
  }, []);

  // Yritä palauttaa istunto refresh-cookiella sivulatauksessa
  useEffect(() => { doRefresh(); }, [doRefresh]);

  // Proaktiivinen token refresh — uusi token joka 12 min
  useEffect(() => {
    if (!state.isAuthenticated) return;
    const interval = setInterval(() => {
      doRefresh();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [state.isAuthenticated, doRefresh]);

  // Kuuntele 401-tapahtumia (apiFetch lähettää kun refresh epäonnistuu)
  useEffect(() => {
    const handler = () => {
      setAccessToken(null);
      void clearApiCache();
      setState(prev => ({ ...prev, isAuthenticated: false, user: null, isLoading: false }));
    };
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  // Identiteettivahti: refresh palautti toisen käyttäjän tokenin → kirjaa tämä
  // tab ulos selkeällä ilmoituksella (EI hiljaista käyttäjänvaihtoa).
  useEffect(() => {
    const handler = () => {
      setAccessToken(null);
      clearTabUser();
      void clearApiCache();
      setState({ isAuthenticated: false, user: null, isLoading: false, authNotice: USER_CHANGED_NOTICE });
    };
    window.addEventListener('auth:user-changed', handler);
    return () => window.removeEventListener('auth:user-changed', handler);
  }, []);

  // BroadcastChannel: toinen tab kirjautui → jos eri käyttäjä, varoita HETI
  // (ilman tätä varoitus tulisi vasta seuraavassa refreshissä, max 12 min).
  useEffect(() => {
    if (!state.isAuthenticated || !state.user) return;
    const currentUserId = state.user.id;
    return onOtherTabLogin((userId) => {
      if (userId !== currentUserId) window.dispatchEvent(new Event('auth:user-changed'));
    });
  }, [state.isAuthenticated, state.user]);

  // Kun käyttäjä palaa välilehdelle tauon jälkeen — refresh heti
  useEffect(() => {
    if (!state.isAuthenticated) return;
    let lastActive = Date.now();
    const handler = () => {
      if (document.visibilityState === 'visible') {
        const away = Date.now() - lastActive;
        if (away > 5 * 60 * 1000) { // Yli 5 min poissa → refresh
          doRefresh();
        }
      }
      lastActive = Date.now();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [state.isAuthenticated, doRefresh]);

  /**
   * Päättää loginin onnistuneen response:n perusteella: tallenna access-token,
   * päivitä user-state ja hae allowedPages /me-endpointista.
   */
  const completeAuthSuccess = useCallback(async (res: LoginResponseSuccess) => {
    setAccessToken(res.accessToken);
    // Identiteettivahti: lukitse tämän välilehden käyttäjä + ilmoita muille
    // tabeille heti (eri käyttäjän tabit varoittavat välittömästi)
    registerTabUser(res.user.id);
    setState({
      isAuthenticated: true,
      user: res.user,
      isLoading: false,
      authNotice: null,
    });
  }, []);

  const login = useCallback(async (creds: LoginRequest): Promise<LoginResult> => {
    const res = await authApi.login(creds);
    if (isLoginRequires2fa(res)) {
      return { status: 'requires2fa', twoFaToken: res.twoFaToken };
    }
    if (isLoginRequiresEnrollment(res)) {
      return { status: 'requiresEnrollment', enrollmentToken: res.enrollmentToken };
    }
    if (isLoginSuccess(res)) {
      await completeAuthSuccess(res);
      return { status: 'ok' };
    }
    throw new Error('Tuntematon login-vaste');
  }, [completeAuthSuccess]);

  const completeTwoFactor = useCallback(async (twoFaToken: string, code: string, trustThisDevice = false) => {
    const res = await twoFactorApi.verify(twoFaToken, code, trustThisDevice);
    await completeAuthSuccess(res);
  }, [completeAuthSuccess]);

  const completeInitialEnrollment = useCallback(
    async (enrollmentToken: string, code: string) => {
      // C7: initial-enroll-confirm aktivoi 2FA:n. Käyttäjän pitää kirjautua
      // uudelleen sen jälkeen (normaali 2FA-flow alkaa nyt).
      const res = await twoFactorApi.initialEnrollConfirm(enrollmentToken, code);
      return { backupCodes: res.backupCodes };
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ei haittaa jos logout epäonnistuu
    }
    setAccessToken(null);
    clearTabUser();
    // Siivoa vanhojen versioiden mahdolliset localStorage-jäännökset
    clearLegacyAuthStorage();
    // P2 (28.7.2026): tyhjennä SW:n api-cache — jaetulla laitteella seuraava
    // käyttäjä ei saa nähdä edellisen cachetettuja API-vastauksia offline-tilassa.
    void clearApiCache();
    setState({ isAuthenticated: false, user: null, isLoading: false, authNotice: null });
  }, []);

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      logout,
      completeTwoFactor,
      completeInitialEnrollment,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
